import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";

const AccountActivation = () => {
  const { id } = useParams();
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");
  const [userInfo, setUserInfo] = useState({ email: "", name: "", org_name: "" });

  // Password creation state
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    const activateAccount = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}activate/${id}`
        );

        const data = await response.json();

        if (!response.ok || data.success === false) {
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
          }

          setStatus("error");
          setMessage(data.message || "Activation link is invalid or has expired.");
          return;
        }

        setStatus("success");
        setMessage(data.message || "Your account has been activated successfully!");
        setUserInfo({
          email: data.email || "",
          name: data.name || "",
          org_name: data.org_name || "",
        });

      } catch (error) {
        setStatus("error");
        setMessage("Something went wrong while activating your account. Please try again or contact support.");
      }
    };

    activateAccount();
  }, [id]);

  const handleCreatePassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (!password) {
      setPasswordError("Password is required.");
      return;
    }
    if (password.length < 6) {
      setPasswordError("Password must be at least 6 characters long.");
      return;
    }
    if (password !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }

    setSavingPassword(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}activate-set-password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: userInfo.email,
            password: password,
          }),
        }
      );

      const data = await response.json();
      if (response.ok && data.success) {
        setPasswordSuccess(data.message || "Password set successfully! You can now log into the mobile app.");
        setPassword("");
        setConfirmPassword("");
      } else {
        setPasswordError(data.message || "Failed to set password. Please try again.");
      }
    } catch (err) {
      setPasswordError("Network error. Please try again.");
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#0d1b2a",
        padding: "40px 20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
      }}
    >
      <div
        style={{
          maxWidth: "540px",
          width: "100%",
          padding: "36px",
          textAlign: "center",
          borderRadius: "16px",
          boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
          backgroundColor: "#1b263b",
          border: "1px solid rgba(76, 161, 175, 0.3)",
          color: "#e0e1dd",
        }}
      >
        {status === "loading" && (
          <div style={{ padding: "40px 0" }}>
            <div
              style={{
                width: "48px",
                height: "48px",
                border: "4px solid rgba(76, 161, 175, 0.2)",
                borderTop: "4px solid #4CA1AF",
                borderRadius: "50%",
                margin: "0 auto 20px auto",
                animation: "spin 1s linear infinite",
              }}
            />
            <h3 style={{ color: "#fff", margin: 0 }}>Activating your account...</h3>
            <p style={{ color: "#8da9c4", fontSize: "14px", marginTop: "8px" }}>Please wait a moment.</p>
          </div>
        )}

        {status === "success" && (
          <div>
            <div
              style={{
                width: "60px",
                height: "60px",
                backgroundColor: "rgba(46, 204, 113, 0.15)",
                border: "2px solid #2ecc71",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px auto",
                fontSize: "28px",
                color: "#2ecc71",
              }}
            >
              ✓
            </div>
            <h2 style={{ color: "#fff", margin: "0 0 8px 0", fontSize: "24px" }}>
              Account Activated!
            </h2>
            <p style={{ color: "#8da9c4", fontSize: "14px", margin: "0 0 24px 0" }}>
              {userInfo.name ? `Welcome, ${userInfo.name}!` : "Welcome to Glocal Prayer Network!"}
              {userInfo.org_name ? ` (${userInfo.org_name})` : ""}
            </p>

            {passwordSuccess ? (
              <div
                style={{
                  backgroundColor: "rgba(46, 204, 113, 0.15)",
                  border: "1px solid #2ecc71",
                  borderRadius: "10px",
                  padding: "20px",
                  marginBottom: "24px",
                }}
              >
                <h4 style={{ color: "#2ecc71", margin: "0 0 8px 0", fontSize: "18px" }}>
                  🎉 {passwordSuccess}
                </h4>
                <p style={{ color: "#e0e1dd", fontSize: "13px", margin: "0 0 16px 0" }}>
                  Download the Glocal Prayer App to log in and begin your prayer journey:
                </p>
                <div style={{ display: "flex", justifyContent: "center", gap: "12px", flexWrap: "wrap", marginBottom: "16px" }}>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.glocalprayer.ep&pcampaignid=web_share"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/thumb/7/78/Google_Play_Store_badge_EN.svg/320px-Google_Play_Store_badge_EN.svg.png"
                      alt="Get it on Google Play"
                      style={{ height: "42px" }}
                    />
                  </a>
                  <a
                    href="https://apps.apple.com/in/app/glocal-prayer-network/id6479648726"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/thumb/3/3c/Download_on_the_App_Store_Badge.svg/320px-Download_on_the_App_Store_Badge.svg.png"
                      alt="Download on App Store"
                      style={{ height: "42px" }}
                    />
                  </a>
                </div>
                <Link
                  to="/login"
                  style={{
                    display: "inline-block",
                    padding: "10px 24px",
                    backgroundColor: "#4CA1AF",
                    color: "#fff",
                    textDecoration: "none",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  Go to Web Login
                </Link>
              </div>
            ) : (
              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.04)",
                  border: "1px solid rgba(76, 161, 175, 0.3)",
                  borderRadius: "12px",
                  padding: "24px",
                  textAlign: "left",
                }}
              >
                <h3 style={{ color: "#4CA1AF", margin: "0 0 6px 0", fontSize: "18px" }}>
                  Create Your Password
                </h3>
                <p style={{ color: "#8da9c4", fontSize: "13px", margin: "0 0 16px 0" }}>
                  Set a convenient password to log into the Glocal Prayer Network mobile app and web portal.
                </p>

                {userInfo.email && (
                  <div style={{ marginBottom: "14px" }}>
                    <label style={{ display: "block", fontSize: "12px", color: "#8da9c4", marginBottom: "4px" }}>
                      Registered Email
                    </label>
                    <input
                      type="text"
                      disabled
                      value={userInfo.email}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        backgroundColor: "rgba(255, 255, 255, 0.08)",
                        border: "1px solid rgba(255, 255, 255, 0.15)",
                        borderRadius: "8px",
                        color: "#fff",
                        fontSize: "14px",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                )}

                <form onSubmit={handleCreatePassword}>
                  <div style={{ marginBottom: "14px" }}>
                    <label style={{ display: "block", fontSize: "12px", color: "#8da9c4", marginBottom: "4px" }}>
                      New Password (min 6 characters) *
                    </label>
                    <div style={{ position: "relative" }}>
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your new password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "10px 40px 10px 12px",
                          backgroundColor: "rgba(255, 255, 255, 0.05)",
                          border: "1px solid rgba(76, 161, 175, 0.4)",
                          borderRadius: "8px",
                          color: "#fff",
                          fontSize: "14px",
                          boxSizing: "border-box",
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          color: "#8da9c4",
                          cursor: "pointer",
                          fontSize: "12px",
                        }}
                      >
                        {showPassword ? "Hide" : "Show"}
                      </button>
                    </div>
                  </div>

                  <div style={{ marginBottom: "18px" }}>
                    <label style={{ display: "block", fontSize: "12px", color: "#8da9c4", marginBottom: "4px" }}>
                      Confirm Password *
                    </label>
                    <div style={{ position: "relative" }}>
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Re-enter password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "10px 40px 10px 12px",
                          backgroundColor: "rgba(255, 255, 255, 0.05)",
                          border: "1px solid rgba(76, 161, 175, 0.4)",
                          borderRadius: "8px",
                          color: "#fff",
                          fontSize: "14px",
                          boxSizing: "border-box",
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          color: "#8da9c4",
                          cursor: "pointer",
                          fontSize: "12px",
                        }}
                      >
                        {showConfirmPassword ? "Hide" : "Show"}
                      </button>
                    </div>
                  </div>

                  {passwordError && (
                    <div
                      style={{
                        backgroundColor: "rgba(244, 67, 54, 0.15)",
                        border: "1px solid #f44336",
                        color: "#ff8a80",
                        padding: "8px 12px",
                        borderRadius: "6px",
                        fontSize: "13px",
                        marginBottom: "14px",
                      }}
                    >
                      {passwordError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={savingPassword}
                    style={{
                      width: "100%",
                      padding: "12px",
                      backgroundColor: savingPassword ? "#2c3e50" : "#4CA1AF",
                      color: "#fff",
                      border: "none",
                      borderRadius: "8px",
                      fontWeight: 600,
                      fontSize: "15px",
                      cursor: savingPassword ? "not-allowed" : "pointer",
                      transition: "background 0.2s ease",
                    }}
                  >
                    {savingPassword ? "Saving Password..." : "Set Password & Complete Setup"}
                  </button>
                </form>

                <div style={{ textAlign: "center", marginTop: "16px" }}>
                  <Link
                    to="/login"
                    style={{
                      color: "#8da9c4",
                      fontSize: "13px",
                      textDecoration: "underline",
                    }}
                  >
                    I already have a password / Go to Login
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}

        {status === "error" && (
          <div>
            <div
              style={{
                width: "60px",
                height: "60px",
                backgroundColor: "rgba(244, 67, 54, 0.15)",
                border: "2px solid #f44336",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px auto",
                fontSize: "28px",
                color: "#f44336",
              }}
            >
              ✕
            </div>
            <h2 style={{ color: "#fff", margin: "0 0 12px 0", fontSize: "22px" }}>
              Activation Link Error
            </h2>
            <p style={{ color: "#e0e1dd", fontSize: "14px", lineHeight: "1.5", margin: "0 0 24px 0" }}>
              {message}
            </p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
              <Link
                to="/forgot-password"
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#4CA1AF",
                  color: "#fff",
                  textDecoration: "none",
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "14px",
                }}
              >
                Reset Password
              </Link>
              <Link
                to="/login"
                style={{
                  padding: "10px 20px",
                  backgroundColor: "rgba(255,255,255,0.1)",
                  color: "#e0e1dd",
                  textDecoration: "none",
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "14px",
                }}
              >
                Go to Login
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountActivation;
